import { useCallback, useEffect, useRef, useState } from "react";
import {
  formDisplayName,
  type AiAssistFormOverview,
  type PatientFormFacts,
  type PatientGroup,
} from "@care-by-ella/contracts";
import {
  AiAssistDisabledError,
  fetchAiFormOverview,
  fetchPatientFormFacts,
  generateAiFormOverview,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { RiskFlagsPanel } from "@/components/RiskFlagsPanel";
import { useReadOnly } from "@/components/SupportView";

// TES-708 — the doctor's form overview (TES-706). Two halves, on purpose
// visually separate: what the SYSTEM computed (facts and flags, top) and what
// the MODEL restated (prose, below, only after "Sammanställ"). Nothing in the
// prose is a number the doctor should trust — the numbers are above it.
//
// TES-943 — the two halves no longer share a switch. The facts and flags come
// from the clinician form-facts route and show whenever the patient has
// submitted anything, AI or not. TES-941:
//   "Översikten får inte vara beroende av att AI-funktionen är aktiverad"
// Only the prose half follows the server
// switch: with it off the ai-assist route 404s and that half is simply absent.
// Form names are the readable ones from the shared contracts map, not codes.

type Ai =
  | { state: "loading" }
  | { state: "disabled" }
  | { state: "error" }
  | { state: "ready"; draft: AiAssistFormOverview["draft"]; stale: boolean };

const nf = new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 });

function dateSv(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

function timeSv(iso: string): string {
  return new Date(iso).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function generateErrorText(e: unknown): string {
  const code = e instanceof Error ? e.message : "";
  switch (code) {
    case "ai_assist_provider_unavailable":
      return "Modellen svarade inte. Försök igen.";
    case "ai_assist_output_rejected":
      return "Utkastet stoppades av skyddsregeln: texten innehöll något som inte fanns i svaren. Försök igen.";
    case "ai_assist_no_input":
      return "Inga formulär att sammanställa än.";
    default:
      return "Kunde inte sammanställa. Försök igen.";
  }
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-sm text-sm">
      <dt className="text-textSecondary">{label}</dt>
      <dd className="text-right font-medium text-textPrimary">{value}</dd>
    </div>
  );
}

function Facts({
  facts,
  formName,
}: {
  facts: PatientFormFacts["facts"];
  formName: (templateKey: string) => string;
}) {
  const w = facts.weight;
  const weightText = w
    ? w.latestKg !== null && w.deltaKg !== null
      ? `${nf.format(w.baselineKg)} → ${nf.format(w.latestKg)} kg (${w.deltaKg > 0 ? "+" : ""}${nf.format(w.deltaKg)} kg, ${formName(w.latestSource ?? "")} ${dateSv(w.latestAt!)})`
      : `${nf.format(w.baselineKg)} kg (${formName(w.baselineSource)} ${dateSv(w.baselineAt)})`
    : "—";
  return (
    <dl className="flex flex-col gap-[2px]">
      <FactRow
        label="Formulär"
        value={
          facts.submissions === 0
            ? "inga"
            : `${facts.submissions} st, senast ${dateSv(facts.latestSubmittedAt!)}`
        }
      />
      <FactRow
        label="Senaste incheckning"
        value={
          facts.daysSinceLastCheckin === null
            ? "ingen"
            : facts.daysSinceLastCheckin === 0
              ? "idag"
              : `${facts.daysSinceLastCheckin} dagar sedan`
        }
      />
      <FactRow label="Vikt" value={weightText} />
      <FactRow
        label="Längd"
        value={
          facts.heightCm === null ? "—" : `${nf.format(facts.heightCm)} cm`
        }
      />
      <FactRow
        label="BMI"
        value={
          facts.bmi
            ? `${nf.format(facts.bmi.value)} (${formName(facts.bmi.source)} ${dateSv(facts.bmi.at)})`
            : "—"
        }
      />
    </dl>
  );
}

// The model writes "# Formulär: …" and "## Rubrik" lines with prose between
// them (prompt v2). Rendered as headings and paragraphs; no other markup is
// expected and none is interpreted.
function Prose({ body }: { body: string }) {
  const blocks = body.split(/\n{2,}/);
  return (
    <div className="flex flex-col gap-sm text-sm leading-relaxed text-textPrimary">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        return (
          <div key={i} className="flex flex-col gap-[2px]">
            {lines.map((line, j) => {
              if (line.startsWith("# ")) {
                return (
                  <h3 key={j} className="font-display text-base mt-xs">
                    {line.slice(2)}
                  </h3>
                );
              }
              if (line.startsWith("## ")) {
                return (
                  <h4
                    key={j}
                    className="text-xs font-semibold uppercase tracking-wide text-textSecondary mt-xs"
                  >
                    {line.slice(3)}
                  </h4>
                );
              }
              return line.trim() === "" ? null : <p key={j}>{line}</p>;
            })}
          </div>
        );
      })}
    </div>
  );
}

export function AiAssistOverviewPanel({
  patientId,
  patientGroup,
  onUnauthorized,
  onAvailability,
  hideFlags = false,
  onFacts,
}: {
  patientId: string;
  // Names F-02 vs F-02B: one `intake` template, two journeys (TES-759).
  patientGroup?: PatientGroup | null;
  onUnauthorized: () => void;
  // TES-709 — tells the page whether the assist exists on this box, so the
  // shorthand box in the documentation column can follow the same switch
  // without a second probe.
  onAvailability?: (available: boolean) => void;
  // TES-948 — the patient overview renders the flags in its own
  // "Riskflaggor: X" list, merged with the open alarms; this panel then shows
  // the facts only, and hands the one response up instead of the page
  // fetching (and access-logging) the same read twice.
  hideFlags?: boolean;
  onFacts?: (facts: PatientFormFacts | null, error: string | null) => void;
}) {
  // TES-1040 — AI is out of scope for the admin's read-only view (its
  // routes are not opened to her): the code-computed facts only, as when the
  // AI switch is off.
  const readOnly = useReadOnly();
  const [facts, setFacts] = useState<PatientFormFacts | null>(null);
  const [factsError, setFactsError] = useState<string | null>(null);
  const [ai, setAi] = useState<Ai>({ state: "loading" });
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // The callbacks are read through a ref so a parent re-render (an inline
  // arrow is a new function each time) does not refetch — every fetch here is
  // a logged patient read.
  const callbacks = useRef({ onUnauthorized, onAvailability, onFacts });
  callbacks.current = { onUnauthorized, onAvailability, onFacts };

  const load = useCallback(() => {
    const { onUnauthorized, onAvailability } = callbacks.current;
    fetchPatientFormFacts(patientId)
      .then((d) => {
        setFacts(d);
        setFactsError(null);
        callbacks.current.onFacts?.(d, null);
      })
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) onUnauthorized();
        else {
          const message = "Kunde inte hämta formulärens uppgifter.";
          setFactsError(message);
          callbacks.current.onFacts?.(null, message);
        }
      });
    if (readOnly) {
      setAi({ state: "disabled" });
      onAvailability?.(false);
      return;
    }
    fetchAiFormOverview(patientId)
      .then((d) => {
        setAi({ state: "ready", draft: d.draft, stale: d.stale });
        onAvailability?.(true);
      })
      .catch((e: unknown) => {
        if (e instanceof AiAssistDisabledError) {
          setAi({ state: "disabled" });
          onAvailability?.(false);
        } else if (e instanceof UnauthorizedError) onUnauthorized();
        else setAi({ state: "error" });
      });
  }, [patientId, readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  async function generate() {
    setGenerating(true);
    setGenerateError(null);
    try {
      const d = await generateAiFormOverview(patientId);
      setAi({ state: "ready", draft: d.draft, stale: d.stale });
      // The same code computed these in the same request; keep them in step.
      setFacts({ facts: d.facts, flags: d.flags });
      onFacts?.({ facts: d.facts, flags: d.flags }, null);
    } catch (e) {
      if (e instanceof AiAssistDisabledError) setAi({ state: "disabled" });
      else if (e instanceof UnauthorizedError) onUnauthorized();
      else setGenerateError(generateErrorText(e));
    } finally {
      setGenerating(false);
    }
  }

  const aiOn = ai.state === "ready" || ai.state === "error";
  const hasSubmissions = (facts?.facts.submissions ?? 0) > 0;
  // Without AI the panel is the facts and flags alone, so with nothing
  // submitted there is nothing to show. With AI it keeps its empty state.
  if (!aiOn && !hasSubmissions && !factsError) return null;

  const formName = (key: string) => formDisplayName(key, patientGroup);
  const draft = ai.state === "ready" ? ai.draft : null;
  const stale = ai.state === "ready" && ai.stale;
  const canGenerate = hasSubmissions && !generating;

  return (
    <section
      className={`bg-surface border rounded-md p-md mb-md ${
        stale ? "border-peach border-l-4" : "border-surfaceMuted"
      }`}
    >
      <div className="flex items-baseline justify-between gap-sm mb-xs">
        <h2 className="font-display text-lg">Formulären i korthet</h2>
        {aiOn ? (
          <span className="text-[11px] uppercase tracking-wide text-textSecondary">
            AI-stöd
          </span>
        ) : null}
      </div>
      <p className="text-xs text-textSecondary mb-sm">
        Siffror och flaggor räknas av systemet.
        {aiOn
          ? " Texten återger bara patientens egna svar — den bedömer inget och föreslår inget."
          : null}
      </p>

      {factsError ? (
        <p className="text-sm text-danger">{factsError}</p>
      ) : !facts ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : (
        <>
          <Facts facts={facts.facts} formName={formName} />
          {hideFlags ? null : (
            // TES-948 — the same clickable list as the overview, without
            // the alarms (their buttons live on the patient overview).
            <div className="mt-sm">
              <RiskFlagsPanel
                flags={facts.flags}
                alarms={[]}
                patientGroup={patientGroup}
              />
            </div>
          )}
        </>
      )}

      {aiOn ? (
        <div className="mt-md border-t border-surfaceMuted pt-sm">
          {ai.state === "error" ? (
            <p className="text-sm text-danger">
              Kunde inte hämta sammanställningen.
            </p>
          ) : draft ? (
            <>
              {stale ? (
                <p className="text-xs font-semibold text-brand-deep mb-xs">
                  Nya svar har kommit in sedan sammanställningen.
                </p>
              ) : null}
              <Prose body={draft.body} />
              <p className="mt-sm text-xs text-textSecondary">
                Genererad {dateSv(draft.generatedAt)}{" "}
                {timeSv(draft.generatedAt)} ur {draft.input.submissions.length}{" "}
                formulär · {draft.model}
              </p>
            </>
          ) : facts && !hasSubmissions ? (
            <p className="text-sm text-textSecondary">Inga formulär ännu.</p>
          ) : (
            <p className="text-sm text-textSecondary">
              Ingen sammanställning gjord än.
            </p>
          )}
          {generateError ? (
            <p className="mt-xs text-sm text-danger">{generateError}</p>
          ) : null}
          <button
            type="button"
            disabled={!canGenerate}
            onClick={() => void generate()}
            className="mt-sm rounded-md bg-brand px-md py-xs text-sm font-semibold text-white disabled:opacity-50"
          >
            {generating
              ? "Sammanställer…"
              : draft
                ? stale
                  ? "Uppdatera"
                  : "Sammanställ igen"
                : "Sammanställ"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
